import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../AuthContext";
import { LoadingContext } from "../LoadingContext";
import "./AdminCustomerVisit.css";
import Swal from "sweetalert2";
import { toLocalDateString } from "../dateUtils";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function AdminCustomerVisit() {
    const { authFetch } = useContext(AuthContext);
    const { setIsLoading } = useContext(LoadingContext);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const requestRef = useRef(null);

    const MySwal = Swal.mixin({
        customClass: {
            container: "my-swal-container",
        },
    });

    const [customDateRange, setCustomDateRange] = useState(() => {
        const today = toLocalDateString(new Date());
        return { startDate: today, endDate: today };
    });

    const [data, setData] = useState([]);

    const [total, setTotal] = useState(0);

    useEffect(() => () => {
        requestRef.current?.abort();
        requestRef.current = null;
        setIsLoading(false);
    }, [setIsLoading]);

    const handleRefresh = () => {
        fetchData();
    };

    const fetchData = async () => {
        const { startDate, endDate } = customDateRange;
        if (!startDate || !endDate || startDate > endDate) {
            MySwal.fire({
                title: "Invalid date range",
                text: "Select a start date and an end date on or after the start date.",
                icon: "warning",
                confirmButtonColor: "#45a049",
            });
            return [];
        }

        requestRef.current?.abort();
        const controller = new AbortController();
        requestRef.current = controller;
        setLoading(true);
        setIsLoading(true);

        try {
            // Date inputs already contain YYYY-MM-DD. Send those calendar dates
            // directly, without a Date/UTC conversion or a hidden time of day.
            const qs = new URLSearchParams({ start: startDate, end: endDate });
            const response = await authFetch(`${API_BASE_URL}/api/Report/customervisit?${qs}`, {
                signal: controller.signal,
            });
            if (!response.ok) {
                throw new Error(`Error fetching : ${response.statusText}`);
            }
            const visits = await response.json();
            if (controller.signal.aborted) return [];
            if (!Array.isArray(visits)) throw new Error("Invalid customer visit response");
            setData(visits);
            setTotal(visits.reduce((sum, visit) => sum + visit.count, 0));
            return visits;
        } catch (error) {
            if (controller.signal.aborted) return [];
            setData([]);
            setTotal(0);
            MySwal.fire({
                title: "Error!",
                text: "Failed to load data. Please try again.",
                icon: "error",
                confirmButtonColor: "#45a049",
            });
            return [];
        } finally {
            if (requestRef.current === controller) {
                requestRef.current = null;
                setLoading(false);
                setIsLoading(false);
            }
        }
    };

    const handleCsvExport = () => {

        try {

            // Must match rowData below exactly - the leading No. column had no header,
            // which shifted every value one column to the right in Excel.
            var csvContent = "No.,Date,Total Visit\n";

            const escapeCSV = (value) => {
                if (value === null || value === undefined) return '';
                const str = String(value);
                if (str.includes(',') || str.includes('"') || str.includes('\n')) {
                    return `"${str.replace(/"/g, '""')}"`;
                }
                return str;
            };

            data.forEach((item, index) => {

                const rowData = [
                    index + 1,
                    escapeCSV(item.date),
                    escapeCSV(item.count),
                ];

                csvContent += rowData.join(',') + "\n";
            });

            // Leading empty cell keeps "Total" under the Date column and the figure
            // under Total Visit
            csvContent += ",Total," + total + "\n";

            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });

            const url = URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.download = `Ardency_CustomerVisit_Report_${toLocalDateString(new Date())}.csv`;

            document.body.appendChild(link);

            // Trigger the download
            link.click();

            // Clean up
            setTimeout(() => {
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }, 100);
        } catch (error) {
            MySwal.fire({
                title: "Error!",
                text: "Failed to export CSV. Please try again.",
                icon: "error",
                confirmButtonColor: "#45a049",
            });
        }
    };

    return (
        <div className="admin-sales-dashboard-container">
            {/* Header Section */}
            <div className="admin-sales-dashboard-header">
                <button
                    className="admin-sales-dashboard-back-btn"
                    onClick={() => navigate("/admin/dashboard")}
                >
                    <i className="fas fa-arrow-left"></i> Back to Dashboard
                </button>
            </div>

            <div className="admin-sales-dashboard-title">
                <h1>Customer Visit Analytics</h1>
            </div>

            <div className="admin-sales-dashboard-refresh" style={{
                marginBottom: '20px',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px'   
            }}>
                <button
                    className="admin-voucher-dashboard-print-btn"
                    onClick={handleCsvExport}
                    disabled={loading}
                >
                    <i className="fas fa-file-export"></i> Export Excel
                </button>
                <button
                    className="admin-sales-dashboard-refresh-btn"
                    onClick={handleRefresh}
                    disabled={loading}
                >
                    <i className="fas fa-sync-alt"></i> Search
                </button>
            </div>


            <div className="admin-sales-dashboard-row">
                <div className="admin-sales-dashboard-overview">
                    <div className="admin-sales-dashboard-print-section">

                        <div className="admin-sales-dashboard-controls">
                            <div className="admin-sales-dashboard-custom-date">
                                <div className="admin-sales-dashboard-control-group">
                                    <label htmlFor="customer-visit-start-date">Start Date</label>
                                    <input
                                        id="customer-visit-start-date"
                                        type="date"
                                        value={customDateRange.startDate || ""}
                                        onChange={(e) =>
                                            setCustomDateRange({
                                                ...customDateRange,
                                                startDate: e.target.value,
                                            })
                                        }
                                    />
                                </div>
                                <div className="admin-sales-dashboard-control-group">
                                    <label htmlFor="customer-visit-end-date">End Date</label>
                                    <input
                                        id="customer-visit-end-date"
                                        type="date"
                                        value={customDateRange.endDate || ""}
                                        onChange={(e) =>
                                            setCustomDateRange({
                                                ...customDateRange,
                                                endDate: e.target.value,
                                            })
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="admin-sales-dashboard-row">

                <div className="admin-sales-dashboard-trends-container"  >
                    <div className="admin-sales-dashboard-print-section">
                        <div className="admin-sales-table-container">
                            {data.length > 0 ? (
                                <table className="admin-sales-table">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Total Visit</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.map((item, index) => (
                                            <tr key={index}>
                                                <td>{item.date}</td>
                                                <td>{item.count}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td><strong>Total</strong></td>
                                            <td><strong>{total}</strong></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            ) : (
                                <div className="admin-sales-empty">
                                    <i className="fas fa-shopping-cart"></i>
                                    <p className="primary-message">
                                        No Customer Visit for the selected period.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AdminCustomerVisit;
